import React from 'react';
import { Badge, Box, BoxProps, Button, Link, Popover, ProgressBar } from '@amzn/awsui-components-react';
import { LeaderboardTeam } from '@/src/types/JamLeaderboard';
import { TFunctionProvider } from '../../common/TFunctionProvider';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { ChallengeMetricsRow } from '@/src/types/JamReport';
import { RANKING_STRATEGIES, RankingStrategyType } from '@/src/constants/shared/ranking-strategy';
import { getPassRate } from '@/src/utils/leaderboard.utils';
import { formatElapsedTime } from '@/src/utils/duration.utils';
import { toOrdinal } from '@/src/utils/number.utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const colorByRank = (rank: number) => {
  switch (rank) {
    case 1:
      return 'gold';
    case 2:
      return 'silver';
    case 3:
      return '#cd7f32';
    default:
      return 'skyblue';
  }
};

const Top3Trophy = ({ rank }: { rank: number }) => {
  switch (rank) {
    case 1:
    case 2:
    case 3:
      return <FontAwesomeIcon icon="trophy" style={{ color: colorByRank(rank) }} />;
    default:
      return null;
  }
};

const getVariant = (rank: number) => (rank <= 3 ? 'h2' : 'h3');
const getClassname = (rank: number) => (rank <= 3 ? 'top-3' : '');

const LeaderboardCell: React.FC<{ rank: number; color?: BoxProps.Color }> = ({ rank, children, ...rest }) => (
  <span className={`leaderboard-cell ${getClassname(rank)}`} {...rest}>
    {children}
  </span>
);

const ScoreBadge: React.FC<{ rank: number; score: number }> = ({ rank, score }) => {
  const rankClass = rank === 1 ? 'gold' : rank === 2 ? 'silver' : rank === 3 ? 'bronze' : '';
  return <span className={`leaderboard__color-badge ${rankClass}`}>{score < 0 ? 0 : score}</span>;
};
export const COLUMN_DEFINITIONS = (
  showTeamDetails: (id: string | null) => void,
  numChallengesInEvent: number,
  showOriginalTeamName: boolean,
  rankingStrategy: RankingStrategyType = RANKING_STRATEGIES.CUMULATIVE_SOLVE_TIME
) => {
  const isMostRecentScoreTime = rankingStrategy === RANKING_STRATEGIES.MOST_RECENT_SCORE_TIME;
  return [
    {
      id: 'rank',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.rank)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ rank }: LeaderboardTeam) => (
        <LeaderboardCell rank={rank}>
          {toOrdinal(rank)} <Top3Trophy rank={rank} />
        </LeaderboardCell>
      ),
    },
    {
      id: 'teamName',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.teamName)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ teamName, teamDisplayName, rank }: LeaderboardTeam) => (
        <Link onFollow={() => showTeamDetails(teamName)}>
          <LeaderboardCell rank={rank} color="text-status-info">
            {showOriginalTeamName ? `${teamDisplayName} (${teamName})` : teamDisplayName}
          </LeaderboardCell>
        </Link>
      ),
    },
    {
      id: 'completedChallenges',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.completedChallenges)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ solvedChallenges, rank }: LeaderboardTeam) => (
        <LeaderboardCell rank={rank}>
          {solvedChallenges}/{numChallengesInEvent}
          {!!numChallengesInEvent && (
            <span className="leaderboard-cell__progress">
              <ProgressBar value={(solvedChallenges * 100) / numChallengesInEvent} />
            </span>
          )}
        </LeaderboardCell>
      ),
    },
    {
      id: 'cluesUsed',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.cluesUsed)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ cluesUsed, rank }: LeaderboardTeam) => <LeaderboardCell rank={rank}>{cluesUsed}</LeaderboardCell>,
    },
    {
      id: 'score',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.score)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ score, rank }: LeaderboardTeam) => (
        <LeaderboardCell rank={rank}>
          <ScoreBadge score={score} rank={rank} />
        </LeaderboardCell>
      ),
    },
    {
      id: 'totalTime',
      header: (
        <Box variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => (
              <>
                {t(i18nKeys.jamDashboard.leaderboard.table.headers.totalTime)}
                <span style={{ marginLeft: '6px' }}>
                  <Popover
                    dismissButton
                    position="top"
                    size="medium"
                    triggerType="custom"
                    header={t(i18nKeys.jamDashboard.leaderboard.table.headers.totalTimeInfo.title)}
                    content={
                      isMostRecentScoreTime
                        ? t(i18nKeys.jamDashboard.leaderboard.table.headers.totalTimeInfo.mostRecent)
                        : t(i18nKeys.jamDashboard.leaderboard.table.headers.totalTimeInfo.cumulative)
                    }
                  >
                    <Button
                      variant="icon"
                      iconName="status-info"
                      ariaLabel={t(i18nKeys.jamDashboard.leaderboard.table.headers.totalTimeInfo.ariaLabel)}
                    />
                  </Popover>
                </span>
              </>
            )}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ totalTime, mostRecentScoreTime, rank }: LeaderboardTeam) => {
        const timeValue = isMostRecentScoreTime ? mostRecentScoreTime : totalTime;
        return <LeaderboardCell rank={rank}>{timeValue != null ? formatElapsedTime(timeValue) : '--'}</LeaderboardCell>;
      },
    },
  ];
};

export const COLUMN_DEFINITIONS_CHALLNGES = (
  challengeTitles: { [key: string]: string },
  showSolvedChallengesStats: (challengeId: string) => void
) => {
  return [
    {
      id: 'title',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.challengeTitle)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ challengeId }: ChallengeMetricsRow) => (
        <Box color="text-status-info" padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {challengeId ? challengeTitles[challengeId] : challengeId}
        </Box>
      ),
    },
    {
      id: 'numTeamsStarted',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.teamsStarted)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ numTeamsStarted }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {numTeamsStarted}
        </Box>
      ),
    },
    {
      id: 'numTeamsAttempted',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.teamsAttempted)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ numTeamsAttempted }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {numTeamsAttempted}
        </Box>
      ),
    },
    {
      id: 'teamsSolved',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.teamsSolved)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ numTeamsSolved, challengeId }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }}>
          {!!numTeamsSolved ? (
            <Button
              variant="inline-link"
              iconName="status-info"
              iconAlign="right"
              data-testid={`challenge-stats-launcher__${challengeId}`}
              onClick={() => showSolvedChallengesStats(challengeId || '')}
            >
              {numTeamsSolved}
            </Button>
          ) : (
            <Box variant="h4" tagOverride="span" display="inline-block">
              {numTeamsSolved}
            </Box>
          )}
        </Box>
      ),
    },
    {
      id: 'incorrectAnswers',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.incorrectAnswers)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ numIncorrectAnswers }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {numIncorrectAnswers}
        </Box>
      ),
    },
    {
      id: 'requestedClues',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>
            {(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.requestedClues)}
          </TFunctionProvider>
        </Box>
      ),
      cell: ({ numCluesRequested }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {numCluesRequested}
        </Box>
      ),
    },
    {
      id: 'passRate',
      header: (
        <Box padding={{ left: 'm' }} variant="h3" tagOverride="span">
          <TFunctionProvider>{(t) => t(i18nKeys.jamDashboard.leaderboard.table.headers.passRate)}</TFunctionProvider>
        </Box>
      ),
      cell: ({ numCompletedTasks, numIncorrectAnswers }: ChallengeMetricsRow) => (
        <Box padding={{ left: 'm' }} variant="h4" tagOverride="span">
          {(getPassRate(numCompletedTasks || 0, numIncorrectAnswers || 0) * 100).toFixed(2)}%
        </Box>
      ),
    },
  ];
};
